import * as React from 'react';
import { PageHeader } from '../../../components/layout/PageHeader.js';
import { Card, CardContent } from '../../../components/ui/Card.js';
import { Button } from '../../../components/ui/Button.js';
import { StatusBadge } from '../../../components/ui/StatusBadge.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/Dialog.js';
import { useToast } from '../../../components/ui/Toast.js';
import { CheckCircle2, AlertTriangle, Clock, Inbox, ShieldAlert, ArrowUpRight } from 'lucide-react';
import {
  useOperationsInbox,
  useOperationsTasks,
  useFailedGlPostings,
  useResolveTask,
  useEscalateTask,
  useResolveFailedGlPosting,
} from '../network/operations.api.js';
import type { OperationalTask, FailedGlPosting, TaskStatus } from '../types.js';

function mapTaskStatus(status: TaskStatus): { status: 'success' | 'warning' | 'danger' | 'info'; label: string } {
  switch (status) {
    case 'RESOLVED':
      return { status: 'success', label: 'RESOLVED' };
    case 'IN_PROGRESS':
      return { status: 'warning', label: 'IN PROGRESS' };
    case 'ESCALATED':
      return { status: 'danger', label: 'ESCALATED' };
    case 'OPEN':
      return { status: 'info', label: 'OPEN' };
    case 'CANCELLED':
      return { status: 'info', label: 'CANCELLED' };
    default:
      return { status: 'info', label: status };
  }
}

export function OperationsInbox() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = React.useState<'inbox' | 'tasks' | 'failed-gl'>('inbox');

  const { data: inboxRes, isLoading: isInboxLoading } = useOperationsInbox();
  const { data: tasksRes, isLoading: isTasksLoading } = useOperationsTasks();
  const { data: failedGlRes, isLoading: isFailedGlLoading } = useFailedGlPostings();

  const resolveTaskMutation = useResolveTask();
  const escalateTaskMutation = useEscalateTask();
  const resolveGlMutation = useResolveFailedGlPosting();

  const [selectedTask, setSelectedTask] = React.useState<OperationalTask | null>(null);
  const [taskResolutionNotes, setTaskResolutionNotes] = React.useState('');
  const [isResolveTaskOpen, setIsResolveTaskOpen] = React.useState(false);

  const [selectedGlPosting, setSelectedGlPosting] = React.useState<FailedGlPosting | null>(null);
  const [glResolutionNotes, setGlResolutionNotes] = React.useState('');
  const [isResolveGlOpen, setIsResolveGlOpen] = React.useState(false);

  const inboxTasks = inboxRes?.data ?? [];
  const allTasks = tasksRes?.data ?? [];
  const failedGlPostings = failedGlRes?.data ?? [];

  const handleResolveTask = async () => {
    if (!selectedTask || !taskResolutionNotes.trim()) return;
    try {
      await resolveTaskMutation.mutateAsync({
        id: selectedTask.id,
        resolutionNotes: taskResolutionNotes.trim(),
      });
      setIsResolveTaskOpen(false);
      setSelectedTask(null);
      setTaskResolutionNotes('');
      toast({
        type: 'success',
        title: 'Task Resolved',
        description: 'The operational task has been successfully marked as RESOLVED.',
      });
    // allow-silent-catch: UI toast notification handles error display
    } catch (err) {
      toast({
        type: 'error',
        title: 'Resolution Failed',
        description: err instanceof Error ? err.message : 'Failed to resolve task.',
      });
    }
  };

  const handleEscalateTask = async (task: OperationalTask) => {
    try {
      await escalateTaskMutation.mutateAsync({ id: task.id });
      toast({
        type: 'success',
        title: 'Task Escalated',
        description: `Task ${task.title} escalated to next priority tier.`,
      });
    // allow-silent-catch: UI toast notification handles error display
    } catch (err) {
      toast({
        type: 'error',
        title: 'Escalation Failed',
        description: err instanceof Error ? err.message : 'Failed to escalate task.',
      });
    }
  };

  const handleResolveGl = async () => {
    if (!selectedGlPosting || !glResolutionNotes.trim()) return;
    try {
      await resolveGlMutation.mutateAsync({
        id: selectedGlPosting.id,
        resolutionNotes: glResolutionNotes.trim(),
      });
      setIsResolveGlOpen(false);
      setSelectedGlPosting(null);
      setGlResolutionNotes('');
      toast({
        type: 'success',
        title: 'Failed GL Posting Resolved',
        description: 'The dead-letter GL posting has been marked as resolved.',
      });
    // allow-silent-catch: UI toast notification handles error display
    } catch (err) {
      toast({
        type: 'error',
        title: 'Resolution Failed',
        description: err instanceof Error ? err.message : 'Failed to resolve failed GL posting.',
      });
    }
  };

  return (
    <div className="flex flex-col w-full h-full space-y-6">
      <PageHeader
        title="Operations Spine & Task Inbox"
        subtitle="Unified operational exception queues, maker-checker SLA breaches, and failed general ledger postings."
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="shadow-sm">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Pending My Action</span>
              <h2 className="text-2xl font-bold text-text-primary">{inboxTasks.length}</h2>
            </div>
            <div className="p-3 bg-accent-500/10 text-accent-500 rounded-lg">
              <Inbox className="w-6 h-6" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Urgent / Escalated</span>
              <h2 className="text-2xl font-bold text-text-primary">
                {allTasks.filter((t) => t.priority === 'URGENT' || t.status === 'ESCALATED').length}
              </h2>
            </div>
            <div className="p-3 bg-warning-bg text-warning-text rounded-lg">
              <AlertTriangle className="w-6 h-6" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="p-6 flex items-center justify-between">
            <div className="space-y-1">
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Failed GL Dead-Letters</span>
              <h2 className="text-2xl font-bold text-text-primary">
                {failedGlPostings.filter((f) => !f.resolvedAt).length}
              </h2>
            </div>
            <div className="p-3 bg-danger-bg text-danger-text rounded-lg">
              <ShieldAlert className="w-6 h-6" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="border-b border-border pb-3 flex gap-3">
        <Button
          variant={activeTab === 'inbox' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setActiveTab('inbox')}
        >
          My Inbox ({inboxTasks.length})
        </Button>
        <Button
          variant={activeTab === 'tasks' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setActiveTab('tasks')}
        >
          All Operational Tasks ({allTasks.length})
        </Button>
        <Button
          variant={activeTab === 'failed-gl' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setActiveTab('failed-gl')}
        >
          Failed GL Postings ({failedGlPostings.length})
        </Button>
      </div>

      {activeTab === 'inbox' || activeTab === 'tasks' ? (
        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
              <tr>
                <th className="p-4">Priority</th>
                <th className="p-4">Task Details</th>
                <th className="p-4">Queue</th>
                <th className="p-4">Status</th>
                <th className="p-4">SLA Deadline</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-text-primary">
              {(activeTab === 'inbox' ? inboxTasks : allTasks).map((task) => (
                <tr key={task.id} className="hover:bg-surface-2 transition-colors">
                  <td className="p-4">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                        task.priority === 'URGENT'
                          ? 'bg-danger-bg text-danger-text'
                          : task.priority === 'HIGH'
                          ? 'bg-warning-bg text-warning-text'
                          : 'bg-surface-2 text-text-secondary'
                      }`}
                    >
                      {task.priority}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="font-semibold text-text-primary">{task.title}</div>
                    <div className="text-xs text-text-muted mt-0.5 line-clamp-1">{task.description}</div>
                  </td>
                  <td className="p-4 font-mono text-xs text-text-secondary">{task.queueName}</td>
                  <td className="p-4">
                    {(() => {
                      const badge = mapTaskStatus(task.status);
                      return <StatusBadge status={badge.status} label={badge.label} />;
                    })()}
                  </td>
                  <td className="p-4 text-xs font-mono">
                    <div className="flex items-center gap-1 text-text-secondary">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(task.slaDeadline).toLocaleString()}
                    </div>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    {task.status !== 'RESOLVED' && task.status !== 'CANCELLED' && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleEscalateTask(task)}
                        >
                          <ArrowUpRight className="w-3.5 h-3.5 mr-1" />
                          Escalate
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => {
                            setSelectedTask(task);
                            setIsResolveTaskOpen(true);
                          }}
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                          Resolve
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(activeTab === 'inbox' ? inboxTasks : allTasks).length === 0 && (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-text-muted">
                    {isInboxLoading || isTasksLoading ? 'Loading operational tasks...' : 'No operational tasks in this view.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
              <tr>
                <th className="p-4">Failed At</th>
                <th className="p-4">Error Cause</th>
                <th className="p-4">Status</th>
                <th className="p-4">Resolution Notes</th>
                <th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-text-primary">
              {failedGlPostings.map((posting) => (
                <tr key={posting.id} className="hover:bg-surface-2 transition-colors">
                  <td className="p-4 text-xs font-mono">{new Date(posting.failedAt).toLocaleString()}</td>
                  <td className="p-4">
                    <div className="text-xs font-mono text-danger-text line-clamp-2">{posting.error}</div>
                  </td>
                  <td className="p-4">
                    <StatusBadge
                      status={posting.resolvedAt ? 'success' : 'danger'}
                      label={posting.resolvedAt ? 'RESOLVED' : 'UNRESOLVED'}
                    />
                  </td>
                  <td className="p-4 text-xs text-text-secondary">
                    {posting.resolutionNotes || '—'}
                  </td>
                  <td className="p-4 text-right">
                    {!posting.resolvedAt && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          setSelectedGlPosting(posting);
                          setIsResolveGlOpen(true);
                        }}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                        Resolve
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {failedGlPostings.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-text-muted">
                    {isFailedGlLoading ? 'Loading failed GL postings...' : 'No failed GL postings recorded.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Task Resolve Dialog */}
      <Dialog open={isResolveTaskOpen} onOpenChange={setIsResolveTaskOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Operational Task</DialogTitle>
            <DialogDescription>
              Record resolution notes for &quot;{selectedTask?.title}&quot;.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <textarea
              className="w-full h-28 p-3 bg-surface-1 border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-500"
              placeholder="Enter resolution steps taken..."
              value={taskResolutionNotes}
              onChange={(e) => setTaskResolutionNotes(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsResolveTaskOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleResolveTask} disabled={!taskResolutionNotes.trim()}>
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Failed GL Resolve Dialog */}
      <Dialog open={isResolveGlOpen} onOpenChange={setIsResolveGlOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve Failed GL Posting</DialogTitle>
            <DialogDescription>
              Provide audit notes confirming manual ledger adjustment or reason for closing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <textarea
              className="w-full h-28 p-3 bg-surface-1 border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-500"
              placeholder="Enter manual journal ID or adjustment details..."
              value={glResolutionNotes}
              onChange={(e) => setGlResolutionNotes(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsResolveGlOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleResolveGl} disabled={!glResolutionNotes.trim()}>
              Mark Resolved
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
